import { datadogLogs } from '@datadog/browser-logs';
import { CheckoutFrameContext } from 'app/context/CheckoutFrameContext';
import { getXprops } from 'app/helpers/getXprops';
import {
  conektaTokenDataInitialValue,
  formCardDataInitialValue,
} from 'app/pages/frames/components/Form/FormCard/constants';
import { IConektaTokenData } from 'app/pages/frames/components/Form/FormCard/interfaces';
import { conektaToken } from 'app/pages/frames/helpers/tokenization';
import { ConektaSource } from 'app/sdk/embedded/EmbeddedConfig';
import { getBinFromCardNumber } from 'app/util/constants';
import get from 'lodash.get';
import { useContext, useState } from 'react';

export const useHandleCard = () => {
  const { checkoutRequest } = useContext(CheckoutFrameContext);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>();
  const [error, setError] = useState<any>();
  const {
    callbacks: { onCreateTokenSucceeded, onCreateTokenError },
    config: { publicKey },
    conektaSource,
  } = getXprops();
  const { needsShippingContact = false, recurrent: isRecurrent = false } = checkoutRequest;

  async function doPostWrapper(props: any) {
    setIsLoading(true);
    const response = await doPost(props);
    setIsLoading(false);
    return response;
  }

  const conektaErrorResponseHandler = (response: any) => {
    onCreateTokenError?.(response);
    datadogLogs.logger.error('tokenizer_error', { error: response });
    const errorTemplate: Record<string, any> = { type: 'error', show: true, message: response?.message_to_purchaser };
    setError(errorTemplate);
    throw errorTemplate;
  };

  const doPost = async (values: any): Promise<any> => {
    try {
      const {
        cardNumber,
        cardVerificationValue,
        cardholderName,
        customerInfo,
        monthlyInstallments,
        shippingContact,
        cardExpMonth,
        cardExpYear,
        ...rest
      } = values;

      const binNumber: string = getBinFromCardNumber(cardNumber);
      const customerInfoName: string = isRecurrent
        ? customerInfo.name
        : get(checkoutRequest, 'orderTemplate.customerInfo.name', '');
      const conektaTokenData: IConektaTokenData = { ...conektaTokenDataInitialValue };
      const tokenId: string = get(checkoutRequest, 'token.tokenId', '');
      conektaTokenData.card = {
        ...formCardDataInitialValue,
        cvc: cardVerificationValue,
        exp_month: cardExpMonth,
        exp_year: cardExpYear,
        name: cardholderName,
        number: cardNumber,
      };

      if (needsShippingContact) {
        conektaTokenData.shippingContact = shippingContact;
        conektaTokenData.shippingContact.receiver = customerInfoName;
      }

      const conektaSuccessResponseHandler = (token: any) => {
        if (conektaSource === ConektaSource.TOKENIZER) {
          datadogLogs.logger.info('tokenizer_success_token');
          onCreateTokenSucceeded?.(token);
          return {};
        } else {
          datadogLogs.logger.info('tokenizer_success');
          return { binNumber, customerInfo, shippingContact, monthlyInstallments, token, ...rest };
        }
      };
      let responseData;
      if (tokenId !== '') {
        conektaTokenData.token_id = tokenId;
        const response = await conektaToken(publicKey, {
          tokenForm: conektaTokenData,
          action: 'put',
          originType: conektaSource,
        });
        responseData = conektaSuccessResponseHandler(response);
        setData(responseData);
      } else {
        const response = await conektaToken(publicKey, {
          tokenForm: conektaTokenData,
          action: 'post',
          originType: conektaSource,
        });
        responseData = conektaSuccessResponseHandler(response);
        setData(responseData);
      }
      return responseData;
    } catch (e) {
      conektaErrorResponseHandler(e);
    }
  };
  return { action: doPostWrapper, loading: isLoading, data, error };
};
